// Return to GINI Code.
//
// This panel never navigates. The GINI Code Artifact Inbox does not exist,
// so there is nothing to hand the artifact to and no allowlisted URL to send
// the browser at — inventing one would be exactly the "claim push delivery
// succeeded" failure this lane must avoid.
//
// What it does instead: asks the daemon for the handoff's own
// contract-governed return target (product + opaque workspace/conversation
// ids, never a URL), renders it as a copyable reference, and states plainly
// that automatic inbox delivery is not connected. The wording is
// "Artifact ready for GINI Code" — ready, not delivered.

import { useState } from 'react';
import { Button } from '@open-design/components';
import type { GiniHandoffCapability, GiniHandoffReturnResponse } from '@open-design/contracts/gini-platform';

import { useT } from '../../i18n';
import { requestHandoffReturn } from '../../providers/gini-handoff';
import { CapabilityNotice } from './CapabilityNotice';
import styles from './HandoffLanding.module.css';

interface Props {
  handoffId: string;
  inbox: GiniHandoffCapability;
  selection: { artifactId: string; artifactVersion: number } | null;
}

/** Bounded, human-carryable reference. Contains ids only — no artifact bytes, no path, no token. */
function formatReference(data: GiniHandoffReturnResponse): string {
  const lines = [
    `product: ${data.returnTarget.product}`,
    `workspace: ${data.returnTarget.workspaceId}`,
    ...(data.returnTarget.conversationId ? [`conversation: ${data.returnTarget.conversationId}`] : []),
    `handoff: ${data.handoffId}`,
    `studioProject: ${data.studioProjectId}`,
    ...(data.artifactId ? [`artifact: ${data.artifactId}`] : []),
    ...(data.artifactVersion !== undefined ? [`artifactVersion: ${data.artifactVersion}`] : []),
  ];
  return lines.join('\n');
}

export function ReturnPanel({ handoffId, inbox, selection }: Props) {
  const t = useT();
  const [result, setResult] = useState<GiniHandoffReturnResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function buildReturn(): Promise<void> {
    setError(null);
    const outcome = await requestHandoffReturn(handoffId, selection ?? {});
    if (outcome.ok) {
      setResult(outcome.data);
    } else {
      setResult(null);
      setError(outcome.message);
    }
  }

  async function copyReference(): Promise<void> {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(formatReference(result));
      setCopied(true);
    } catch {
      // Clipboard can be denied; the reference is already on screen and
      // selectable, so this is not worth surfacing as an error.
      setCopied(false);
    }
  }

  return (
    <section className={styles.card} aria-labelledby="gini-handoff-return">
      <h2 className={styles.cardTitle} id="gini-handoff-return">
        {t('giniHandoff.returnHeading')}
      </h2>

      <p className={styles.empty} data-testid="gini-handoff-return-ready">
        {t('giniHandoff.returnReady')}
      </p>

      <CapabilityNotice capability={inbox} testId="gini-handoff-inbox-notice" />

      <div className={styles.actions}>
        <Button type="button" variant="primary" onClick={() => void buildReturn()} data-testid="gini-handoff-return-action">
          {t('giniHandoff.returnAction')}
        </Button>
        {result ? (
          <Button type="button" variant="subtle" onClick={() => void copyReference()} data-testid="gini-handoff-copy-reference">
            {copied ? t('giniHandoff.copied') : t('giniHandoff.copyReference')}
          </Button>
        ) : null}
      </div>

      {error ? (
        <p className={styles.empty} data-testid="gini-handoff-return-error">
          {error}
        </p>
      ) : null}

      {result ? (
        <>
          <pre className={styles.reference} data-testid="gini-handoff-return-reference">
            {formatReference(result)}
          </pre>
          <p className={styles.empty} data-testid="gini-handoff-inbox-note">
            {result.inboxUnavailableReason}
          </p>
        </>
      ) : null}
    </section>
  );
}
